import React, {
  useEffect,
  useMemo,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import IconButton from '@mui/material/IconButton';
import Skeleton from 'core/components/Skeleton';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  useFormWorkflowDetail,
  useFormWorkflowPdfs,
} from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/hooks';
import WorkflowEncounterDisplay from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/WorkflowEncounterDisplay';
import Section from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/Section';
import Row from 'pages/Dashboard/components/Row';
import {
  useMedia,
  useSidePanel,
} from 'core/hooks';
import Name from 'pages/Dashboard/components/Name';
import StatusTag from 'core/components/StatusTag';
import Tag from 'core/components/Tag';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  formatDateTime,
} from 'utils/date';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  FormWorkflowStatus,
} from 'dtos/formWorkflowStatus';
import {
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import usePreviewMediaClick from 'pages/Dashboard/hooks/usePreviewMediaClick';
import workflowStatusMap from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/constants';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import Button from '@populateui/ui/button';
import compact from 'lodash/compact';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';

type WorkflowMediaPreviewProps = {
  isLoading: boolean;
  mediaList: MultiMediaResponse[];
};

function WorkflowMediaPreview({
  isLoading,
  mediaList,
}: WorkflowMediaPreviewProps) {
  const handleMultiMediaClick = usePreviewMediaClick();

  return isLoading ? (
    <div className="flex flex-col gap-2 p-3">
      <Skeleton className="!h-8 w-full" />
      <Skeleton className="!h-8 w-full" />
    </div>
  ) : (
    <div className="max-h-[14rem] overflow-y-auto flex flex-col gap-2 p-3">
      {mediaList.map((media) => (
        <Button
          key={media.mediaId}
          onClick={() => {
            handleMultiMediaClick(media);
          }}
          color="primary"
          className="!normal-case !py-1.5 !px-3 !rounded-full !border-gray-200 !bg-white hover:!bg-blue-50 !w-full !justify-start"
          variant="outlined"
        >
          <AttachFileRoundedIcon className="!w-4 !h-4 !text-blue-500" />
          <span className="!text-xs !text-gray-700 !font-medium truncate">
            {media.fileName ?? 'View media'}
          </span>
        </Button>
      ))}
    </div>
  );
}

type RecipientRowProps = {
  label: string;
  value?: string | null;
};

function RecipientRow({
  label,
  value,
}: RecipientRowProps) {
  return (
    <div className="flex items-center gap-3 py-2 text-xs border-b border-gray-100 last:border-b-0">
      <span className="text-gray-500 font-medium w-16 shrink-0">{label}</span>
      <span className="font-medium text-gray-900 break-all">{value}</span>
    </div>
  );
}

export type FormWorkflowPanelInfo = { formWorkflowId: number };

export default function FormWorkflowDetailPanel({
  onClose,
  entity: { formWorkflowId: workflowId },
  slots,
}: SidePanelComponentProps<FormWorkflowPanelInfo>) {
  const { data, isLoading } = useFormWorkflowDetail(workflowId);
  const { getMediaPathById, requestMedia } = useMedia();
  const { openPanel } = useSidePanel();

  const workflow = data?.formWorkflow;
  const patient = data?.patient;
  const isCompleted = workflow?.status === FormWorkflowStatus.Completed;

  const assignedClinicianSrc = useUserProfilePicturePath(workflow?.assignedClinicianUser?.userId);
  const initiatedBySrc = useUserProfilePicturePath(workflow?.initiatedByUser?.userId);

  useEffect(() => {
    const userIds = compact([
      workflow?.assignedClinicianUser?.userId,
      workflow?.initiatedByUser?.userId,
    ]);
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }, [workflow?.assignedClinicianUser?.userId, workflow?.initiatedByUser?.userId, requestMedia]);

  const { data: mediaData, isLoading: isLoadingMedia } = useFormWorkflowPdfs(
    workflowId,
    isCompleted,
  );

  const hasCompletedMedia = !isNil(head(mediaData?.list ?? []));
  const showCompletedFormsSection = isCompleted && (isLoadingMedia || hasCompletedMedia);

  const recipientRows = useMemo(() => {
    const recipient = workflow?.patientRecipient;
    if (isNil(recipient)) {
      return [];
    }

    return [
      { label: 'Email', value: recipient.email },
      { label: 'SMS', value: recipient.sms },
      { label: 'Phone', value: recipient.phone },
    ].filter((row) => !isEmpty(row.value));
  }, [workflow?.patientRecipient]);

  const workflowStatusDisplayName = workflow?.statusDisplayName ?? '';
  const workflowCreatedOn = workflow?.createdOn ?? '';
  const workflowUpdatedOn = workflow?.updatedOn ?? '';

  return (
    <div className="flex flex-col w-full bg-white rounded-xl justify-between h-full">
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex flex-row justify-between w-full items-start py-4 px-6 border-b border-gray-200 bg-gray-50/60">
          <div className="flex flex-col gap-1.5">
            <h1 className="font-semibold text-xl text-gray-900">
              Form Workflow
            </h1>
            {notEmptyString(workflowStatusDisplayName) && (
              <StatusTag
                value={workflowStatusDisplayName}
                statusMap={workflowStatusMap}
                className="w-fit"
              />
            )}
          </div>
          <div className="flex items-center">
            {slots?.actions}
            <IconButton onClick={onClose} className="!-mr-1">
              <CloseIcon className="!text-xl text-gray-500" />
            </IconButton>
          </div>
        </div>

        <div className="px-4 py-4 flex flex-col gap-3 overflow-y-auto flex-1">
          <Section title="General information">
            <Row title="Patient" isLoading={isLoading}>
              <Name
                className="!text-xs !w-[initial]"
                link={getPatientChartPath(patient?.patientId)}
                name={getDisplayName(patient?.formattedNames)}
                image={{ src: getMediaPathById(patient?.patientId ?? 0) ?? undefined }}
              />
            </Row>
            <Row title="Status" isLoading={isLoading}>
              {notEmptyString(workflowStatusDisplayName) ? (
                <StatusTag
                  value={workflowStatusDisplayName}
                  statusMap={workflowStatusMap}
                />
              ) : DEFAULT_EMPTY_VALUE}
            </Row>
            <Row title="Assigned clinician" isLoading={isLoading}>
              {!isNil(workflow?.assignedClinicianUser) ? (
                <Name
                  className="!w-[initial] ml-auto"
                  name={getDisplayName(workflow.assignedClinicianUser.formattedNames)}
                  image={{ src: assignedClinicianSrc }}
                />
              ) : DEFAULT_EMPTY_VALUE}
            </Row>
            <Row title="Encounter" isLoading={isLoading}>
              <WorkflowEncounterDisplay
                encounter={workflow?.encounter}
                className="ml-auto"
              />
            </Row>
            <Row title="Created on" isLoading={isLoading}>
              <span className="text-gray-900">
                {notEmptyString(workflowCreatedOn) ? formatDateTime(workflowCreatedOn) : DEFAULT_EMPTY_VALUE}
              </span>
            </Row>
            <Row title="Created by" isLoading={isLoading}>
              {!isNil(workflow?.initiatedByUser) ? (
                <Name
                  className="!w-[initial] ml-auto"
                  name={getDisplayName(workflow.initiatedByUser.formattedNames)}
                  image={{ src: initiatedBySrc }}
                />
              ) : DEFAULT_EMPTY_VALUE}
            </Row>
            <Row title="Updated on" isLoading={isLoading}>
              <span className="text-gray-900">
                {notEmptyString(workflowUpdatedOn) ? formatDateTime(workflowUpdatedOn) : DEFAULT_EMPTY_VALUE}
              </span>
            </Row>
            {!isNil(workflow?.toDoId) && (
              <div className="px-2 py-3">
                <Button
                  variant="contained"
                  color="primary"
                  className="!normal-case"
                  onClick={() => {
                    const toDoId = workflow?.toDoId ?? 0;
                    if (!isNilOrZero(toDoId)) {
                      openPanel('todo', { todoId: toDoId });
                    }
                  }}
                >
                  Open linked To-do
                </Button>
              </div>
            )}
          </Section>

          <Section title="Attached forms">
            {isLoading ? (
              <div className="p-3">
                <Skeleton className="!h-14 w-full" />
                <Skeleton className="!h-14 w-full mt-2" />
              </div>
            ) : (
              <div className="max-h-[11rem] overflow-y-auto flex flex-col divide-y divide-gray-100">
                {(workflow?.allocations ?? []).map((allocation) => (
                  <div
                    key={allocation.formId}
                    className="flex items-start gap-3 px-3 py-3 text-xs transition-colors hover:bg-gray-50"
                  >
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                      <DescriptionOutlinedIcon className="!w-4 !h-4" />
                    </div>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 leading-snug">
                          {allocation.title}
                        </span>
                        {allocation.isArchived && (
                          <Tag
                            tagName="Archived"
                            defaultColor="bg-gray-200"
                            className="shrink-0 !font-semibold !text-gray-700"
                          />
                        )}
                      </div>
                      <span className="text-gray-500">
                        {allocation.category?.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {!isEmpty(recipientRows) && (
            <Section title="Patient recipient">
              <div className="px-3 py-1">
                {recipientRows.map((row) => (
                  <RecipientRow
                    key={row.label}
                    label={row.label}
                    value={row.value}
                  />
                ))}
              </div>
            </Section>
          )}

          {showCompletedFormsSection && (
            <Section title="Completed forms">
              <WorkflowMediaPreview
                isLoading={isLoadingMedia}
                mediaList={mediaData?.list ?? []}
              />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
